import React from 'react';
import { Link } from 'react-router-dom';
import PublicLayout from '../PublicLayout';
import { CONTACT_EMAIL, LEGAL_LAST_UPDATED, LegalBlock, LegalDocument, privacyPolicy, termsOfUse } from './legalContent';

// Renders a legalContent.ts document: draft banner, title, table of contents, sections.
// [Placeholders] are highlighted; the contact email, OAIC site and cross-page names become links.
const TOKENS = new RegExp(`(\\[[^\\]]+\\]|${CONTACT_EMAIL.replace(/\./g, '\\.')}|www\\.oaic\\.gov\\.au|Privacy Policy)`, 'g');

const renderInline = (text: string) =>
    text.split(TOKENS).map((part, i) => {
        if (part.startsWith('[') && part.endsWith(']')) return <mark key={i} className="legal-placeholder">{part}</mark>;
        if (part === CONTACT_EMAIL) return <a key={i} href={`mailto:${CONTACT_EMAIL}`}>{part}</a>;
        if (part === 'www.oaic.gov.au') return <a key={i} href="https://www.oaic.gov.au" target="_blank" rel="noopener noreferrer">{part}</a>;
        if (part === 'Privacy Policy') return <Link key={i} to="/privacy">{part}</Link>;
        return <React.Fragment key={i}>{part}</React.Fragment>;
    });

const renderBlock = (block: LegalBlock, i: number) =>
    typeof block === 'string'
        ? <p key={i}>{renderInline(block)}</p>
        : <ul key={i}>{block.list.map((item, j) => <li key={j}>{renderInline(item)}</li>)}</ul>;

const LegalPage = ({ doc }: { doc: LegalDocument }) =>
    <PublicLayout pageTitle={doc.pageTitle} className="legal-page">
        <div className="legal-draft" role="note">
            <span className="legal-draft-icon" aria-hidden="true">!</span>
            <p><strong>Draft</strong> — this page has not been reviewed by a lawyer yet. Last updated {LEGAL_LAST_UPDATED}.</p>
        </div>
        <article className="legal-card">
            <h1>{doc.title}</h1>
            <p className="legal-updated">Last updated {LEGAL_LAST_UPDATED}</p>
            <p className="legal-summary">{doc.summary}</p>
            <nav className="legal-toc" aria-labelledby="legal-toc-heading">
                <h2 id="legal-toc-heading">On this page</h2>
                <ol>
                    {doc.sections.map(s => <li key={s.id}><a href={`#${s.id}`}>{s.heading}</a></li>)}
                </ol>
            </nav>
            {doc.sections.map(s =>
                <section key={s.id} id={s.id} className="legal-section" aria-labelledby={`${s.id}-heading`}>
                    <h2 id={`${s.id}-heading`}>{s.heading}</h2>
                    {s.blocks.map(renderBlock)}
                </section>)}
        </article>
    </PublicLayout>;

export const PrivacyPage = () => <LegalPage doc={privacyPolicy} />;
export const TermsPage = () => <LegalPage doc={termsOfUse} />;

export default LegalPage;
